import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ServiceModalAction } from '../../actions/ServiceModalAction';
import { Translate as t } from '../../config/Translate';
import { formatDisplayDateTime } from '../../lib/dateDisplay';

const PRIORITY_OPTIONS = [
	{ value: 'URGENT', labelKey: 'urgent', icon: '🔴' },
	{ value: 'FAVORITE', labelKey: 'favorite', icon: '⭐' },
	{ value: 'NORMAL', labelKey: 'normal', icon: '🟢' },
];

const ServiceModalIssueAlert = ({ alert, assigneeList, loading, GetAssigneeAvailability, ProcessAlert, onClose }) => {
	const [priority, setPriority] = useState('URGENT');
	const [assignee, setAssignee] = useState('');
	const [showDone, setShowDone] = useState(false);
	const [doneTicketKey, setDoneTicketKey] = useState('');
	const [isProcessing, setIsProcessing] = useState(false);

	useEffect(() => {
		if (alert && alert.service && alert.service.companyId) {
			GetAssigneeAvailability(alert.service.companyId);
		} else if (alert && alert.companyId) {
			GetAssigneeAvailability(alert.companyId);
		}
		// 기존 priority/assignee 값이 있으면 유지, 없으면 기본값
		setPriority(alert?.service?.priority || 'URGENT');
		setAssignee(alert?.service?.assignee || '');
	}, [alert]);

	if (!alert && !showDone) return null;

	// 완료 메시지 표시 중
	if (showDone) {
		return (
			<div style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '40px 16px',
				textAlign: 'center',
			}}>
				<div style={{ fontSize: '36px', marginBottom: '12px' }}>✅</div>
				<div style={{ fontSize: '15px', fontWeight: 600, color: '#198754', marginBottom: '6px' }}>
					{t('service.issueManager.appliedSuccess')}
				</div>
				<div style={{ fontSize: '13px', color: '#6c757d' }}>
					{doneTicketKey}
				</div>
			</div>
		);
	}

	const isNewTicket = !alert.level || alert.level === 0;
	const levelColor = isNewTicket ? '#0d6efd' : alert.level === 1 ? '#dc3545' : alert.level === 2 ? '#fd7e14' : '#ffc107';
	const badgeLabel = isNewTicket ? t('service.issueManager.newTicket') : `${t('service.issueManager.issueAlert')} — Level ${alert.level}`;

	const handleProcess = async () => {
		if (!assignee || isProcessing) return;
		setIsProcessing(true);
		const tk = alert.ticketKey || '';
		const aid = alert._id;
		const sid = alert.serviceId;
		const pri = priority;
		const asg = assignee;
		setDoneTicketKey(tk);
		setShowDone(true);
		await ProcessAlert(aid, sid, pri, asg, tk);
		setTimeout(() => {
			setShowDone(false);
			setIsProcessing(false);
		}, 1000);
	};

	const labelStyle = {
		fontWeight: 600,
		color: '#495057',
		marginBottom: '2px',
		fontSize: '13px',
	};

	const valueStyle = {
		color: '#212529',
		fontSize: '14px',
		marginBottom: '10px',
	};

	return (
		<div style={{ display: 'flex', gap: '20px', padding: '16px' }}>
			{/* 좌측: 접수 정보 */}
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ marginBottom: '12px' }}>
					<span style={{
						display: 'inline-block',
						padding: '4px 10px',
						backgroundColor: levelColor,
						color: '#fff',
						borderRadius: '4px',
						fontSize: '12px',
						fontWeight: 600,
					}}>
						{badgeLabel}
					</span>
				</div>

				<div style={labelStyle}>{t('service.issueManager.ticketKey')}</div>
				<div style={valueStyle}>
					{alert.ticketKey ? (
						<Link
							to={`/Admin/AdminService/${alert.ticketKey}`}
							onClick={onClose}
							style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 500 }}
						>
							{alert.ticketKey} <ion-icon name="open-outline" style={{ fontSize: '12px', verticalAlign: 'middle' }}></ion-icon>
						</Link>
					) : '-'}
				</div>

				<div style={labelStyle}>{t('service.issueManager.summary')}</div>
				<div style={valueStyle}>{alert.summary || '-'}</div>

				<div style={labelStyle}>{t('service.issueManager.description')}</div>
				<div style={{
					...valueStyle,
					maxHeight: '80px',
					overflow: 'auto',
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
					fontSize: '13px',
					color: '#495057',
				}}>
					{alert.service?.description || '-'}
				</div>

				<div style={labelStyle}>{t('service.issueManager.alertType')}</div>
				<div style={valueStyle}>{alert.alertType || '-'}</div>

				<div style={labelStyle}>{t('service.issueManager.createdDate')}</div>
				<div style={valueStyle}>
					{alert.service?.createDate
						? formatDisplayDateTime(alert.service.createDate)
						: alert.createDate
							? formatDisplayDateTime(alert.createDate)
							: '-'}
				</div>
			</div>

			{/* 우측: Priority + Assignee + 처리 버튼 */}
			<div style={{ width: '220px', flexShrink: 0, borderLeft: '1px solid #dee2e6', paddingLeft: '20px' }}>
				<div style={labelStyle}>{t('service.issueManager.priority')}</div>
				<div style={{ marginBottom: '14px' }}>
					{PRIORITY_OPTIONS.map(opt => (
						<label
							key={opt.value}
							style={{
								display: 'flex',
								alignItems: 'center',
								cursor: 'pointer',
								padding: '4px 0',
								fontSize: '13px',
							}}
						>
							<input
								type="radio"
								name="priority"
								value={opt.value}
								checked={priority === opt.value}
								onChange={() => setPriority(opt.value)}
								style={{ marginRight: '8px' }}
							/>
							{opt.icon} <span style={{ marginLeft: '4px' }}>{t(opt.labelKey)}</span>
						</label>
					))}
				</div>

				<div style={labelStyle}>{t('service.issueManager.assignee')}</div>
				<select
					className="form-select form-select-sm"
					value={assignee}
					onChange={(e) => setAssignee(e.target.value)}
					style={{ marginBottom: '16px' }}
				>
					<option value="">{t('service.issueManager.selectAssignee')}</option>
					{assigneeList.map(user => (
						<option key={user.adminid} value={user.adminid}>
							{user.availability === 'AVAILABLE' ? '🟢 ' : '⚫ '}
							{user.name || user.adminid}
							{user.availability !== 'AVAILABLE' ? ` (${t('service.issueManager.offDuty')})` : ''}
						</option>
					))}
				</select>

				<button
					className="btn btn-primary btn-sm w-100"
					onClick={handleProcess}
					disabled={!assignee || loading || isProcessing}
				>
					{loading ? t('service.issueManager.processing') : t('service.issueManager.apply')}
				</button>
			</div>
		</div>
	);
};

const mapState = (state) => ({
	assigneeList: state.ServiceModalReducer.assigneeList,
	loading: state.ServiceModalReducer.loading,
});

const mapDispatch = (dispatch) => ({
	GetAssigneeAvailability: (companyId) => dispatch(ServiceModalAction.GetAssigneeAvailability(companyId)),
	ProcessAlert: (alertId, serviceId, priority, assignee) =>
		dispatch(ServiceModalAction.ProcessAlert(alertId, serviceId, priority, assignee)),
});

export default connect(mapState, mapDispatch)(ServiceModalIssueAlert);
